import React, { useEffect, useState } from 'react'
import {
  Checkbox,
  createStyles,
  FormControlLabel,
  makeStyles,
  Typography,
  useTheme,
} from '@material-ui/core'
import { connect } from 'react-redux'
import { useRouter } from 'next/router'
import { Cart, GAEvent } from '@/typings/base'
import { createCheckoutPayment } from '@/redux/actions/checkout'
import { useMatomo } from '@datapunt/matomo-tracker-react'
import useCommonStyles from '../../components/PaymentForm/CardForm.style'
import Button from '../Button/Button'
import AdyenCheckout from '@adyen/adyen-web'
import { isSubscription } from '@/utils/constants'
import { postDadPayment, postSubPayment } from '@/redux/actions/payment'
import { numberToPrice } from '@/utils/utils'
import clsx from 'clsx'
import Link from '../Link/Link'
import CMSText, { prismicText } from '../Global/CMSText'
import Loader from '../Loader/Loader'
import * as gtag from '../../utils/gtag'
import {
  clearItemVariantFromGAParamsIfNotChalhoub,
  getPaymentInfoGtmData,
  isChalhoub,
  safeDataLayerPush,
} from '@/utils/gtag'
import useRetractationEffect from './hook/useRetractationEffect'

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {},
    label: {
      marginTop: theme.spacing(2),
      fontSize: 10,
      display: 'block',
      paddingLeft: theme.spacing(1),
    },
    input: {
      display: 'block',
      width: '100%',
      height: `${theme.spacing(2) + 16}px`,
      padding: theme.spacing(1, 1.25),
      background: theme.extraColors.greyLight,
      border: `1px solid ${theme.extraColors.greyLight}`,
      borderRadius: theme.spacing(1),
      fontFamily: theme.typography.fontFamily,
      '&::placeholder': {
        opacity: 0.75,
        color: theme.typography.body1.color,
      },
    },
    marginBottom: {
      marginBottom: theme.spacing(1),
    },
    mainContainer: {
      alignItems: 'center',
    },
    row: {
      display: 'flex',
      justifyContent: 'space-between',
      width: '100%',
      gap: theme.spacing(2),
    },
  })
)

type Tprops = {
  cart: Cart
  t: any
  text: any
  user: any
  settings: any
  postDadPayment: (data: object, token: string) => any
  postSubPayment: (data: object, token: string) => any
  createCheckoutPayment: (token: string) => any
  startLeasingDate?: string
  coupon?: string
  swikId?: string
  disabled?: boolean
}

const PaymentFormAdyen = ({
  cart,
  postDadPayment,
  postSubPayment,
  t,
  settings,
  text,
  createCheckoutPayment,
  startLeasingDate,
  coupon,
  swikId,
  disabled,
}: Tprops) => {
  const classes = useStyles()
  const commonClasses = useCommonStyles()
  const theme = useTheme()
  const router = useRouter()
  const [isCardValidAdyen, setIsCardValidAdyen] = useState(false)
  const [adyenCardData, setAdyenCardData] = useState(null)
  const [userAgent, setUserAgent] = useState('')
  const [paymentProcessing, setPaymentProcessing] = useState(false)
  const [paymentFailed, setPaymentFailed] = useState(true)
  const [holderName, setHolderName] = useState('')
  const [cgu, setCgu] = useState(false)
  const [retractReady, setRetractReady] = useState(false)
  const [retractAgree, setRetractAgree] = useState(false)
  const [cguError, setCguError] = useState(false)
  const [error, setError] = useState(null)
  const [registeredStoreSlug, setRegisteredStoreSlug] = useState('')
  const isRetractation = text?.enable_retractation_agreement
  const retractingDays = text?.retractation_duration
  const { trackEvent, pushInstruction } = useMatomo()

  useEffect(() => {
    typeof localStorage !== 'undefined' &&
      setRegisteredStoreSlug(
        JSON.parse(localStorage.getItem('selectedStore'))?.slug
      )
  }, [])

  const changeCgu = () => {
    setCgu(!cgu)
  }

  const changeRetract = () => {
    setRetractAgree(!retractAgree)
  }

  const handleHolderName = (event) => {
    setHolderName(event?.target?.value)
  }

  useRetractationEffect({
    startLeasingDate,
    isRetractation,
    retractingDays,
    setRetractReady,
    setRetractAgree,
  })

  const configuration = {
    locale: cart?.item?.locale,
    environment: process.env.ADYEN_ENVIRONNEMENT,
    clientKey:
      process.env.IS_SEPARATE_ADYEN === 'true' &&
      cart?.item?.items[0]?.type === 'RENTAL'
        ? process.env.ADYEN_RENTAL_CLIENT_KEY
        : process.env.ADYEN_CLIENT_KEY,
    onChange: handleOnChange,
  }

  function handleOnChange(state) {
    if (!state.isValid) return
    if (state.isValid) {
      setIsCardValidAdyen(state.isValid)
      setAdyenCardData(state.data.paymentMethod)
      setUserAgent(state.data.browserInfo.userAgent)
    }
  }

  const handleAdyenOrder = () => {
    const asyncFunc = async () => {
      if (cgu && retractAgree && holderName) {
        setCguError(false)
        setPaymentProcessing(true)
        trackEvent({
          category: 'Payment',
          action: `User click on pay button`,
        })
        if (isCardValidAdyen) {
          await createCheckoutPayment(cart.item.tokenValue)
          const linkLocal = router.locale ? `/${router.locale}` : ''
          const data = {
            email: localStorage.getItem('email'),
            userAgent: userAgent,
            card: {
              number: adyenCardData.encryptedCardNumber,
              expiryMonth: adyenCardData.encryptedExpiryMonth,
              cvc: adyenCardData.encryptedExpiryYear,
              expiryYear: adyenCardData.encryptedSecurityCode,
              type: adyenCardData.type,
              holderName: holderName,
            },
            returnUrl: window.location.origin,
            frontSuccessPaymentUrl: `${window.location.origin}${linkLocal}/confirmation`,
            frontFailPaymentUrl: `${window.location.origin}${linkLocal}/payment?error_3D=true`,
            swik_id: swikId,
          }

          if (
            cart?.item?.shipments[0].method?.type !== 'HOME_DELIVERY' &&
            typeof localStorage.getItem('rmsParcelShopId') !== 'undefined' &&
            localStorage.getItem('rmsParcelShopId') !== null
          ) {
            data['parcelShopId'] = localStorage.getItem('rmsParcelShopId')
          }
          let payment = null
          if (!isSubscription) {
            payment = await postDadPayment(data, cart.item.tokenValue)
          } else {
            payment = await postSubPayment(data, cart.item.tokenValue)
          }
          if (payment.paymentResult === 'Order completed') {
            setPaymentProcessing(false)
            setPaymentFailed(false)
            // On NE supprime plus 'CartToken' ici : la page /confirmation en a
            // besoin pour récupérer la commande (token + email) puis le nettoie
            // via emptyCart(). Cohérent avec Stripe/Checkout.
            // localStorage.removeItem('CartToken')
            // https://matomo.org/docs/ecommerce-analytics/#how-to-set-up-ecommerce-tracking-in-matomo
            await pushInstruction(
              'trackEcommerceOrder',
              cart?.item?.tokenValue, // orderId (Required) – String – A unique reference numbe to avoid duplication.
              numberToPrice(cart?.item?.totals?.total), // revenue (Required) – Integer/Float – The order total including tax & shipping with any discounts subtracted.
              numberToPrice(
                cart?.item?.totals?.total - cart?.item?.totals?.shipping
              ), // subTotal (Optional) – Integer/Float – The order total excluding shipping.
              numberToPrice(cart?.item?.totals?.taxes), // tax (Optional) – Integer/Float -The amount of tax charged.
              numberToPrice(cart?.item?.totals?.shipping), // shipping (Optional) – Integer/Float – The amount charged for shipping.
              numberToPrice(cart?.item?.totals?.promotion) // discount (Optional) – Integer/Float/Boolean – Discount offered? Default to false. Otherwise, you should include a numeric value.
            )

            const gaParams = {
              transaction_id: cart?.item?.tokenValue,
              currency: cart.item.currency,
              value: numberToPrice(cart?.item?.totals?.total),
              tax: numberToPrice(cart?.item?.totals?.taxes),
              shipping: numberToPrice(cart?.item?.totals?.shipping),
              items: cart.item.items.map(({ product, quantity, total }) => {
                const firstVariant = Object.values(product?.variants)[0]
                return {
                  ['item_name']: product.name,
                  ['item_id']: isChalhoub ? firstVariant.sku : product.code,
                  ['item_variant']: isChalhoub && firstVariant.code,
                  price: numberToPrice(total),
                  quantity,
                }
              }),
            }

            const cleanGAParams =
              clearItemVariantFromGAParamsIfNotChalhoub(gaParams)
            gtag.tracker(GAEvent.PURCHASE, cleanGAParams)

            safeDataLayerPush(getPaymentInfoGtmData(cart, coupon, 'adyen'))

            // Meta Pixel: Purchase event with amount and currency
            if (
              typeof window !== 'undefined' &&
              typeof window.fbq === 'function'
            ) {
              window.fbq('track', 'Purchase', {
                value: numberToPrice(cart?.item?.totals?.total),
                currency: cart?.item?.currency,
              })
            }

            await router.push('/confirmation')
          } else if (payment?.paymentResult?.resultCode === 'RedirectShopper') {
            localStorage.setItem('Payment type', '3dSecure')
            const checkout = await AdyenCheckout(configuration)
            checkout
              .createFromAction(payment.paymentResult.action)
              .mount('#customCard-container')
          } else {
            setError(t('texts:funnel:payment:error'))
            setPaymentProcessing(false)
          }
        }
      } else {
        setCguError(true)
      }
    }
    asyncFunc()
  }

  useEffect(() => {
    if (process.env.USE_ADYEN && cart?.item) {
      const asyncFunc = async () => {
        // Create an instance of AdyenCheckout using the configuration object.
        const checkout = await AdyenCheckout(configuration)

        // Create an instance of Drop-in and mount it to the container you created.
        checkout
          .create('securedfields', {
            // Optional configuration
            type: 'card',
            brands: ['mc', 'visa', 'amex', 'bcmc', 'maestro'],
            styles: {
              base: {
                lineHeight: theme.typography.body2.lineHeight,
                fontFamily: `${theme.typography.fontFamily}`,
                fontSize: '14px',
                color: theme.typography.body1.color,
                WebkitAppearance: 'none',
              },
              label: {
                marginTop: theme.spacing(1),
                display: 'block',
              },
              error: {
                WebkitAppearance: 'none',
                color: theme.palette.error.main,
                background: theme.extraColors.greyLight,
              },
              validated: {
                WebkitAppearance: 'none',
                color: theme.palette.success.main,
                background: theme.extraColors.greyLight,
              },
              placeholder: {
                color: theme.typography.body1.color,
                fontWeight: 400,
                opacity: 0.75,
              },
            },
          })
          .mount('#customCard-container')
        //}
      }
      asyncFunc()
    }
  }, [cart])

  return (
    <div className={classes.mainContainer}>
      <div className={classes.root} id={'root'}>
        <>
          <div id="customCard-container">
            <label>
              <Typography
                className={classes.label}
                component={'span'}
                variant={'body1'}
              >
                <CMSText
                  asText
                  data={settings?.card_number}
                  defaultText={t('texts:general:card_number')}
                />
              </Typography>
              <span
                data-cse="encryptedCardNumber"
                className={clsx(classes.input, 'input--ayden')}
              ></span>
            </label>
            <div className={classes.row}>
              <label>
                <Typography
                  className={classes.label}
                  component={'span'}
                  variant={'body1'}
                >
                  <CMSText
                    asText
                    data={settings?.expiry_date}
                    defaultText={t('texts:general:expiry_date')}
                  />
                </Typography>
                <span
                  data-cse="encryptedExpiryDate"
                  className={clsx(classes.input, 'input--ayden')}
                ></span>
              </label>
              <label>
                <Typography
                  className={classes.label}
                  component={'span'}
                  variant={'body1'}
                >
                  <CMSText
                    asText
                    data={settings?.cvc_code}
                    defaultText={t('texts:general:cvc_code')}
                  />
                </Typography>
                <span
                  data-cse="encryptedSecurityCode"
                  className={clsx(classes.input, 'input--ayden')}
                ></span>
              </label>
            </div>
            <label>
              <Typography
                className={classes.label}
                component={'span'}
                variant={'body1'}
              >
                <CMSText
                  asText
                  data={settings?.cvc_code}
                  defaultText={t('texts:general:holder_name')}
                />
              </Typography>
              <input
                type="text"
                name="holderName"
                className={clsx(
                  classes.input,
                  classes.marginBottom,
                  'input--ayden'
                )}
                placeholder={t('texts:general:holder_name')}
                value={holderName}
                required={true}
                onChange={() => handleHolderName(event)}
              />
            </label>
          </div>
        </>
      </div>
      <FormControlLabel
        control={
          <Checkbox
            checked={cgu}
            onChange={changeCgu}
            name="checkedA"
            data-testid={'cgu_check'}
          />
        }
        label={
          <Typography
            variant={'body2'}
            component={'span'}
            className={commonClasses.cgu}
          >
            <CMSText
              data={text?.cgu}
              defaultText={t('texts:funnel:payment:cgu')}
            />
          </Typography>
        }
      />
      {registeredStoreSlug && (
        <Typography
          variant={'body2'}
          component={'span'}
          className={commonClasses.customCgl}
        >
          <Link href={`/pages/cgl-${registeredStoreSlug}`}>
            <a>
              <CMSText
                data={text?.custom_cgu_link}
                asText
                defaultText={t('texts:funnel:payment:custom_cgu_link')}
              />
            </a>
          </Link>
        </Typography>
      )}
      {isRetractation && retractReady && (
        <FormControlLabel
          control={
            <Checkbox
              checked={retractAgree}
              onChange={changeRetract}
              name="retractation"
              data-testid={'retract_check'}
            />
          }
          label={
            <Typography
              variant={'body2'}
              component={'span'}
              className={commonClasses.cgu}
            >
              <CMSText
                data={text?.retractation_agreement}
                defaultText={t('texts:funnel:payment:retractation_agreement')}
              />
            </Typography>
          }
        />
      )}
      {paymentFailed && (
        <Typography color={'error'} variant={'body1'} align={'center'}>
          {error}
        </Typography>
      )}
      {cguError && (
        <Typography color={'secondary'} variant={'body1'} align={'center'}>
          <CMSText
            asText
            data={text?.cgu_error}
            defaultText={t('texts:funnel:payment:cgu_error')}
          />
        </Typography>
      )}
      <div className={commonClasses.centeredButton}>
        <Button
          disabled={
            !cgu ||
            !retractAgree ||
            !isCardValidAdyen ||
            paymentProcessing ||
            holderName === '' ||
            disabled
          }
          className={clsx(
            commonClasses.submit,
            {
              [commonClasses.inProgress]: paymentProcessing,
              'payment-form__submit-progress': paymentProcessing,
            },
            'payment-form__submit'
          )}
          variant="contained"
          data-testid="paymentSubmit"
          color="primary"
          onClick={handleAdyenOrder}
          type="submit"
        >
          <span
            className={
              paymentProcessing ? commonClasses.hide : commonClasses.show
            }
          >
            {prismicText(text?.pay, t('texts:funnel:pay'))}
          </span>
          {paymentProcessing && <Loader />}
        </Button>
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  cart: state.cart,
})

const mapDispatchToProps = {
  postDadPayment: postDadPayment,
  postSubPayment: postSubPayment,
  createCheckoutPayment: createCheckoutPayment,
}

export default connect(mapStateToProps, mapDispatchToProps)(PaymentFormAdyen)
